import { asQuestionnaireInstanceId, type QuestionnaireInstanceId } from "@repo/domain";
import { z } from "zod";
import { unitSchema } from "./common.schemas";
import { newEntityIdSchema } from "./entity-id.schemas";
import { patientIdSchema } from "./patient.schemas";

const memberIdSchema = z.string().regex(/^[a-z][a-z0-9]*-[1-9]\d*$/);
export const questionnaireInstanceIdSchema: z.ZodType<QuestionnaireInstanceId, z.ZodTypeDef, string> = z
  .string()
  .min(1)
  .transform(asQuestionnaireInstanceId);
export const questionnaireAnswerContentSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("Quantity"), value: z.number().finite(), unit: unitSchema.optional() }),
  z.object({ kind: z.literal("Integer"), value: z.number().int() }),
  z.object({ kind: z.literal("Choice"), optionId: memberIdSchema.optional(), freeText: z.string().optional() }),
  z.object({ kind: z.literal("Boolean"), value: z.boolean() }),
  z.object({ kind: z.literal("Text"), value: z.string() }),
  z.object({ kind: z.literal("Date"), value: z.string() }),
  z.object({
    kind: z.literal("Attachment"),
    url: z.string(),
    contentType: z.string().optional(),
    title: z.string().optional(),
  }),
  z.object({ kind: z.literal("Url"), value: z.string() }),
]);

export const assignQuestionnaireInputSchema = z.object({
  id: newEntityIdSchema,
  definitionUrl: z.string().url(),
  patientId: patientIdSchema,
});
export type AssignQuestionnaireInput = z.infer<typeof assignQuestionnaireInputSchema>;
export const startQuestionnaireFlowInputSchema = z.object({
  instanceId: newEntityIdSchema,
  id: newEntityIdSchema,
  flowUrl: z.string().url(),
  patientId: patientIdSchema,
});
export type StartQuestionnaireFlowInput = z.infer<typeof startQuestionnaireFlowInputSchema>;
export const recordQuestionnaireAnswerInputSchema = z.object({
  instanceId: questionnaireInstanceIdSchema,
  questionId: memberIdSchema,
  occurrence: z.number().int().nonnegative().optional(),
  value: questionnaireAnswerContentSchema,
});
export type RecordQuestionnaireAnswerInput = z.infer<typeof recordQuestionnaireAnswerInputSchema>;
export const clearQuestionnaireAnswerInputSchema = z.object({
  instanceId: questionnaireInstanceIdSchema,
  questionId: memberIdSchema,
  occurrence: z.number().int().nonnegative().optional(),
});
export type ClearQuestionnaireAnswerInput = z.infer<typeof clearQuestionnaireAnswerInputSchema>;
export const completeQuestionnaireInputSchema = z.object({ instanceId: questionnaireInstanceIdSchema });
export type CompleteQuestionnaireInput = z.infer<typeof completeQuestionnaireInputSchema>;
export const reopenQuestionnaireInputSchema = z.object({
  instanceId: questionnaireInstanceIdSchema,
  reason: z.string().trim().min(1),
});
export type ReopenQuestionnaireInput = z.infer<typeof reopenQuestionnaireInputSchema>;
export const questionnaireInstanceIdInputSchema = z.object({ instanceId: questionnaireInstanceIdSchema });
export type QuestionnaireInstanceIdInput = z.infer<typeof questionnaireInstanceIdInputSchema>;
